/**
 * Helper functions for navigation mapping
 */

/**
 * Recursively searches an object tree to find an item by name
 * @param {Object} obj - Object tree to search
 * @param {string} name - Name to search for
 * @return {Object|null} Found item or null
 */
import { sanitizeText } from "@/utils/textUtils"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"

const websiteOrigin = process.env.NEXT_PUBLIC_WEBSITE_URL || ""

export const findItemByName = (obj, name) => {
  // Check if obj is null/undefined first
  if (!obj) return {}

  // If we have :items, check them first
  if (obj[":items"]) {
    // Look through each key in :items
    for (const key of Object.keys(obj[":items"])) {
      // If key contains our search name
      if (key.includes(name)) {
        return obj[":items"][key]
      }
      // If this item has nested :items, recurse into them
      const result = findItemByName(obj[":items"][key], name)
      if (result) return result
    }
  }
  // Return empty object instead of null to allow safe property access
  return null
}

export const hasHeaderItems = header => {
  if (header[":items"]) {
    for (const key of Object.keys(header[":items"])) {
      if (
        header[":items"][key][":items"] &&
        Object.keys(header[":items"][key][":items"]).length > 0
      ) {
        return true
      }
    }
  }
  return false
}

export const addStoreDetailsInteractionHandlers = (
  navigationProps,
  loadStoreDetails
) => {
  const locationPickerCTA = navigationProps?.utilityBar?.locationPickerCTA

  if (!locationPickerCTA || typeof loadStoreDetails !== "function") {
    return navigationProps
  }

  const addHandler = existingHandler => event => {
    loadStoreDetails()
    existingHandler?.(event)
  }

  return {
    ...navigationProps,
    utilityBar: {
      ...navigationProps.utilityBar,
      locationPickerCTA: {
        ...locationPickerCTA,
        onMouseEnter: addHandler(locationPickerCTA.onMouseEnter),
        onFocus: addHandler(locationPickerCTA.onFocus),
        onClick: addHandler(locationPickerCTA.onClick),
      },
    },
  }
}

/**
 * Extracts menu items from a navigation object
 * @param {Object} obj - Navigation object
 * @return {Array} Array of menu items
 */
export function getMenuItems(obj) {
  const result = []

  // Check if object has itemsOrder
  if (obj === null || !obj[":itemsOrder"]) return result

  // Iterate through itemsOrder
  for (const itemKey of obj[":itemsOrder"]) {
    const item = obj[":items"][itemKey]

    // For each item, check its :items
    if (item[":items"]) {
      // Look for keys containing globalnavigation_v3
      for (const key of Object.keys(item[":items"])) {
        if (key.includes("globalnavigation_v3")) {
          result.push({
            ...item[":items"][key],
            appliedCssClassNames: item.appliedCssClassNames ?? "",
          })
        }
      }
    }
  }

  return result
}

const checkForBusinessLevelOneArray = obj => {
  if (!obj?.levelOneNavMulti) return false

  return obj.levelOneNavMulti.some(levelOne => {
    if (!levelOne?.levelTwoNavMulti) return false

    // Check if all levelTwo items have empty levelThreeNavMulti arrays
    return levelOne.levelTwoNavMulti.every(levelTwo => {
      return levelTwo?.levelThreeNavMulti?.length === 0
    })
  })
}

export const transformArray = (obj, tabTitle) => {
  const res = []
  obj["levelOneNavMulti"]?.forEach(item => {
    const levelTwoArray = []
    item?.levelTwoNavMulti?.forEach(levelTwo => {
      levelTwoArray.push({
        title: {
          trackingAttributes: {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: modifyHref(levelTwo.groupLink)
                ? "true"
                : "false",
              internalLinkName: `${tabTitle} > ${sanitizeText(
                levelTwo.groupTitle
              )}`.toLowerCase(),
              internalLinkURL: levelTwo.groupLink
                ? `${
                    levelTwo.groupLink.indexOf("http") === -1
                      ? websiteOrigin
                      : ""
                  }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                : "n/a",
            }),
          },
          text: sanitizeText(levelTwo.groupTitle),
          href: modifyHref(levelTwo.groupLink),
          target: levelTwo.newTabGroup ? "_blank" : "_self",
        },
        isCTA: levelTwo.groupTitle?.includes("carrot-icon"),

        items:
          levelTwo.levelThreeNavMulti?.length > 0
            ? levelTwo.levelThreeNavMulti?.map(levelThree => {
                return {
                  trackingAttributes: {
                    "data-datalayer-id": "kds-element-navigation",

                    "data-datalayer-data": JSON.stringify({
                      clickInternalLinks: modifyHref(levelThree.link)
                        ? "true"
                        : "false",
                      internalLinkName: `${tabTitle} > ${sanitizeText(
                        levelTwo.groupTitle
                      )} > ${sanitizeText(levelThree.label)}`?.toLowerCase(),
                      internalLinkURL: levelThree.link
                        ? `${
                            levelThree.link.indexOf("http") === -1
                              ? websiteOrigin
                              : ""
                          }${modifyHref(levelThree.link)}`.toLowerCase()
                        : "n/a",
                    }),
                  },
                  text: sanitizeText(levelThree.label),
                  href: modifyHref(levelThree.link),
                  target: levelThree.newTab ? "_blank" : "_self",
                }
              })
            : undefined,
      })
    })

    res.push(levelTwoArray)
  })
  return res
}

export const transformBusinessArray = (obj, tabTitle) => {
  const res = []
  obj["levelOneNavMulti"]?.forEach(item => {
    // Check if this levelOne item has all empty levelThreeNavMulti arrays
    if (checkForBusinessLevelOneArray({ levelOneNavMulti: [item] })) {
      // If true, create a simplified structure with just titles and links
      const simplifiedStructure = {
        items:
          item.levelTwoNavMulti?.map(levelTwo => ({
            title: {
              text: sanitizeText(levelTwo.groupTitle),
              href: modifyHref(levelTwo.groupLink),
              trackingAttributes: {
                "data-datalayer-id": "kds-element-navigation",
                "data-datalayer-data": JSON.stringify({
                  clickInternalLinks: modifyHref(levelTwo.groupLink)
                    ? "true"
                    : "false",
                  internalLinkName: `${tabTitle} > ${sanitizeText(
                    levelTwo.groupTitle
                  )}`?.toLowerCase(),
                  internalLinkURL: levelTwo.groupLink
                    ? `${
                        levelTwo.groupLink.indexOf("http") === -1
                          ? websiteOrigin
                          : ""
                      }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                    : "n/a",
                }),
              },
            },
            newTabGroup: levelTwo.newTabGroup,
          })) || [],
      }

      res.push(simplifiedStructure)
    } else {
      // If false, keep the existing structure with tracking attributes
      const levelTwoArray = []
      item?.levelTwoNavMulti?.forEach(levelTwo => {
        levelTwoArray.push({
          title: {
            text: sanitizeText(levelTwo.groupTitle),
            href: modifyHref(levelTwo.groupLink),
            target: levelTwo.newTabGroup ? "_blank" : "_self",
            trackingAttributes: {
              "data-datalayer-id": "kds-element-navigation",
              "data-datalayer-data": JSON.stringify({
                clickInternalLinks: modifyHref(levelTwo.groupLink)
                  ? "true"
                  : "false",
                internalLinkName: `${tabTitle} > ${sanitizeText(
                  levelTwo.groupTitle
                )}`?.toLowerCase(),
                internalLinkURL: levelTwo.groupLink
                  ? `${
                      levelTwo.groupLink.indexOf("http") === -1
                        ? websiteOrigin
                        : ""
                    }${modifyHref(levelTwo.groupLink)}`?.toLowerCase()
                  : "n/a",
              }),
            },
          },
          items:
            levelTwo.levelThreeNavMulti?.length > 0
              ? levelTwo.levelThreeNavMulti?.map(levelThree => ({
                  text: sanitizeText(levelThree.label),
                  href: modifyHref(levelThree.link),
                  target: levelThree.newTab ? "_blank" : "_self",
                  trackingAttributes: {
                    "data-datalayer-id": "kds-element-navigation",

                    "data-datalayer-data": JSON.stringify({
                      clickInternalLinks: modifyHref(levelThree.link)
                        ? "true"
                        : "false",
                      internalLinkName: `${tabTitle} > ${sanitizeText(
                        levelTwo.groupTitle
                      )} > ${sanitizeText(levelThree.label)}`.toLowerCase(),
                      internalLinkURL: levelThree.link
                        ? `${
                            levelThree.link.indexOf("http") === -1
                              ? websiteOrigin
                              : ""
                          }${modifyHref(levelThree.link)}`?.toLowerCase()
                        : "n/a",
                    }),
                  },
                }))
              : undefined,
        })
      })
      res.push(levelTwoArray)
    }
  })
  return res
}

/**
 * Gets panel titles from a navigation object
 * @param {Object} obj - Navigation object
 * @return {Array<string>} Array of panel titles
 */
export function getPanelTitles(obj) {
  // Check if object has required properties
  if (obj === null || !obj[":itemsOrder"] || !obj[":items"]) return []

  // Get an array of titles
  const titles = obj[":itemsOrder"].map(key => {
    const item = obj[":items"][key]
    return item["cq:panelTitle"] || ""
  })

  return titles
}
